/**
 * SegmentBuilderDialog
 *
 * Full-screen authoring surface that promotes the Segment Builder from a
 * collapsed panel at the bottom of the Audience Manager to a top-bar
 * action with a starter-templates rail. Body mounts the existing
 * SegmentBuilder component unchanged — just with optional initialFilter /
 * initialName props that prefill the form when an operator clicks a
 * starter template.
 *
 * Each starter template is a real filter_json tree compiled against the
 * SegmentResolver field whitelist (see backend/services/segmentResolver.js)
 * so the previews actually run when the dialog opens.
 */

import React, { useLayoutEffect, useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Dialog,
  IconButton,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { brandColors, brandFonts } from '../../../theme/brandColors';
import SegmentBuilder, { SegmentBuilderInitialFilter } from '../SegmentBuilder';

/** Prefill from MarketingAudiencesPage StarterChooser (`audiences.json` filterJson). */
export interface SegmentBuilderStarterSeed {
  name: string;
  description?: string;
  filter: SegmentBuilderInitialFilter;
}

export interface SegmentBuilderDialogProps {
  open: boolean;
  onClose: () => void;
  /** Re-run the parent's saved-segments fetch when the dialog closes. */
  onSaved?: () => void;
  /** When set as the dialog opens, loads this filter + name into the builder (starter tile optional). */
  starterSeed?: SegmentBuilderStarterSeed | null;
}

interface StarterTemplate {
  key: string;
  name: string;
  category: 'activation' | 'conversion' | 'lifecycle' | 'dormant';
  description: string;
  filter: SegmentBuilderInitialFilter;
}

const TEMPLATES: StarterTemplate[] = [
  {
    key: 'trial_zero_chapters_idle',
    name: 'Trial users · 0 chapters · 7+ days idle',
    category: 'activation',
    description: 'Free or trial members who registered, never wrote a chapter, and have not touched the app in a week. The onboarding-nudge funnel\'s primary target.',
    filter: {
      op: 'AND',
      conditions: [
        { field: 'users.is_active', op: 'eq', value: true },
        { field: 'users.subscription_plan', op: 'in', value: ['free', 'trial'] },
        { field: 'free_tier_users_analytics.total_chapters', op: 'eq', value: 0 },
        { field: 'users.updated_at', op: 'before_days', value: 7 },
      ],
    },
  },
  {
    key: 'hot_no_ai',
    name: 'Hot tier · no AI usage yet',
    category: 'conversion',
    description: 'Active, engaged members who have never tried the AI text-transformation. A one-email "try AI" walkthrough usually unlocks them.',
    filter: {
      op: 'AND',
      conditions: [
        { field: 'users.is_active', op: 'eq', value: true },
        { field: 'free_tier_users_analytics.engagement_tier', op: 'eq', value: 'hot' },
        { field: 'free_tier_users_analytics.ai_uses', op: 'eq', value: 0 },
      ],
    },
  },
  {
    key: 'premium_pitch',
    name: '3+ chapters · score ≥ 60',
    category: 'conversion',
    description: 'Multi-chapter free-tier members with the highest conversion scores. The premium-pitch broadcast audience.',
    filter: {
      op: 'AND',
      conditions: [
        { field: 'users.is_active', op: 'eq', value: true },
        { field: 'free_tier_users_analytics.total_chapters', op: 'gte', value: 3 },
        { field: 'free_tier_users_analytics.conversion_score', op: 'gte', value: 60 },
      ],
    },
  },
  {
    key: 'marketing_eligible',
    name: 'Verified · email opted-in',
    category: 'lifecycle',
    description: 'Default safe-broadcast audience: confirmed email + explicit marketing opt-in. Use as a base layer for any one-off send.',
    filter: {
      op: 'AND',
      conditions: [
        { field: 'users.is_active', op: 'eq', value: true },
        { field: 'users.email_verified', op: 'eq', value: true },
        { field: 'user_settings.email_opt_in', op: 'eq', value: true },
      ],
    },
  },
  {
    key: 'lapsed_winback',
    name: 'Win-back · 1+ chapter · idle 30+d',
    category: 'dormant',
    description: 'Members who started writing then stopped. Strongest re-engagement signal — they already proved intent.',
    filter: {
      op: 'AND',
      conditions: [
        { field: 'users.is_active', op: 'eq', value: true },
        { field: 'free_tier_users_analytics.total_chapters', op: 'gte', value: 1 },
        { field: 'free_tier_users_analytics.latest_chapter_at', op: 'before_days', value: 30 },
      ],
    },
  },
  {
    key: 'cancel_comeback',
    name: 'Cancel comeback · cancelling at period end',
    category: 'dormant',
    description: 'Paid subscribers whose Stripe subscription is set to cancel at period end. The last-chance retention audience — usually responsive to a discount.',
    filter: {
      op: 'AND',
      conditions: [
        { field: 'users.is_active', op: 'eq', value: true },
        { field: 'stripe_subscriptions.cancel_at_period_end', op: 'eq', value: true },
      ],
    },
  },
];

const CATEGORY_COLOR: Record<StarterTemplate['category'], string> = {
  activation: brandColors.forestGreen,
  conversion: brandColors.richBurgundy,
  lifecycle: brandColors.deepTeal,
  dormant: brandColors.slateGray,
};

const SegmentBuilderDialog: React.FC<SegmentBuilderDialogProps> = ({
  open,
  onClose,
  onSaved,
  starterSeed,
}) => {
  const [active, setActive] = useState<StarterTemplate | null>(null);
  const [builderKey, setBuilderKey] = useState(0);

  const applyTemplate = (tpl: StarterTemplate) => {
    setActive(tpl);
    setBuilderKey((k) => k + 1);
  };

  useLayoutEffect(() => {
    if (!open || !starterSeed) return;
    setActive({
      key: 'starter-chooser',
      name: starterSeed.name,
      category: 'lifecycle',
      description: starterSeed.description ?? '',
      filter: starterSeed.filter,
    });
    setBuilderKey((k) => k + 1);
  }, [open, starterSeed]);

  const handleClose = () => {
    setActive(null);
    setBuilderKey(0);
    onClose();
    onSaved?.();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen
      PaperProps={{ sx: { bgcolor: brandColors.softCream } }}
    >
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: brandColors.deepTeal,
          color: brandColors.white,
        }}
      >
        <Toolbar sx={{ gap: 2 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: brandFonts.heading,
                fontWeight: 700,
                fontSize: '1.25rem',
                lineHeight: 1.15,
              }}
            >
              Custom segment builder
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontFamily: brandFonts.body,
                opacity: 0.85,
                display: 'block',
              }}
            >
              Filter members across users, engagement, email events, and Stripe — saved
              segments become targets for funnels and ad-hoc sends.
            </Typography>
          </Box>
          <IconButton onClick={handleClose} sx={{ color: brandColors.white }}>
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '320px 1fr' },
          gap: 0,
          flex: 1,
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            p: 2.5,
            bgcolor: brandColors.white,
            borderRight: { md: `1px solid ${brandColors.deepTeal}1A` },
            borderBottom: { xs: `1px solid ${brandColors.deepTeal}1A`, md: 'none' },
            overflowY: 'auto',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <AutoAwesomeIcon sx={{ color: brandColors.warmGoldDark, fontSize: 20 }} />
            <Typography
              sx={{
                fontFamily: brandFonts.heading,
                fontWeight: 700,
                color: brandColors.deepTeal,
              }}
            >
              Starter templates
            </Typography>
          </Box>
          <Typography
            variant="caption"
            sx={{ display: 'block', mb: 2, color: brandColors.slateGray, fontFamily: brandFonts.body }}
          >
            Click a template to prefill the builder. Edit any field once it loads.
          </Typography>
          <Stack spacing={1.25}>
            {TEMPLATES.map((tpl) => {
              const accent = CATEGORY_COLOR[tpl.category];
              const isActive = active?.key === tpl.key;
              return (
                <Tooltip
                  key={tpl.key}
                  arrow
                  placement="right"
                  title={
                    <Box sx={{ p: 0.5, maxWidth: 280 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {tpl.name}
                      </Typography>
                      <Typography variant="caption">{tpl.description}</Typography>
                    </Box>
                  }
                >
                  <Box
                    onClick={() => applyTemplate(tpl)}
                    sx={{
                      cursor: 'pointer',
                      p: 1.5,
                      borderRadius: 2,
                      border: `1px solid ${accent}33`,
                      borderLeft: `4px solid ${accent}`,
                      bgcolor: isActive ? `${accent}14` : brandColors.white,
                      transition: 'background-color 120ms, transform 120ms',
                      '&:hover': { bgcolor: `${accent}0F`, transform: 'translateX(2px)' },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: brandFonts.body,
                        fontWeight: 600,
                        color: brandColors.deepTeal,
                        mb: 0.25,
                      }}
                    >
                      {tpl.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        color: brandColors.slateGray,
                        fontFamily: brandFonts.body,
                      }}
                    >
                      {tpl.description}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })}
          </Stack>
          <Button
            fullWidth
            size="small"
            onClick={() => applyTemplate({
              key: 'blank',
              name: '',
              category: 'lifecycle',
              description: '',
              filter: { op: 'AND', conditions: [{ field: 'users.is_active', op: 'eq', value: true }] },
            } as StarterTemplate)}
            sx={{
              mt: 2,
              fontFamily: brandFonts.accent,
              fontWeight: 600,
              textTransform: 'none',
              color: brandColors.slateGray,
              '&:hover': { bgcolor: `${brandColors.deepTeal}0A` },
            }}
          >
            Or start from a blank canvas
          </Button>
        </Box>

        <Box sx={{ p: { xs: 2, md: 3 }, overflowY: 'auto' }}>
          <SegmentBuilder
            key={builderKey}
            initialFilter={active?.filter}
            initialName={active && active.key !== 'blank' ? active.name : undefined}
            autoOpenEditor={!!active}
          />
        </Box>
      </Box>
    </Dialog>
  );
};

export default SegmentBuilderDialog;
